import { Mediator } from '@spotahome/soyuz-mediator';
import { trans } from '@spotahome/soyuz/client';
import { BILLS_INCLUDED_VALUES, CHANNELS } from '@spotahome/ui-library';
import { getBillsIncluded as getBillsIncludedUtil } from '@spotahome/ui-library/src/utils/listing';

const VALUE_PROPOSITION_ICONS = {
  depositProtection: 'lock_closed',
  noDeposit: 'savings',
  isChecked: 'check_circle_transparent',
  instantBooking: 'electricity_bill',
  billsIncluded: 'savings',
  someBillsIncluded: 'savings',
  longTerm: 'calendar_month',
  landlordType: 'key_locker',
  lastMinuteBackUp: 'support',
  gracePeriodCancellation: 'history',
  check24Hours: 'mystery',
  landlordVerified: 'verified'
};

const handleDepositProtectionClick = () => {
  Mediator.publish(CHANNELS.OPEN_DEPOSIT_PROTECTION_MODAL);
};

const handleVerificationClick = () => {
  Mediator.publish(CHANNELS.OPEN_VERIFICATION_MODAL);
};

const handleGracePeriodCancellationClick = () => {
  Mediator.publish(CHANNELS.OPEN_GRACE_PERIOD_CANCELLATION_MODAL);
};

const handleInstantBookingClick = () => {
  Mediator.publish(CHANNELS.OPEN_INSTANT_BOOKING_MODAL);
};

const handleLastMinuteBackupClick = () => {
  Mediator.publish(CHANNELS.OPEN_LAST_MINUTE_BACKUP_MODAL);
};

const handleCheck24HoursClick = () => {
  Mediator.publish(CHANNELS.OPEN_CHECK_PLACE_24H_MODAL);
};

const handleSpotahomeGuaranteeClick = ({ title, body }) => {
  Mediator.publish(CHANNELS.OPEN_GUARANTEE_MODAL, { title, body });
};

/**
 * Builds chip list for ListingChipsContainer from the same data as ValuePropositionFeatures.
 * All possible chips are included; each has an `include` boolean for later filtering.
 * Elements are in the desired display order.
 * @param {Object} params
 * @param {Object} params.listing - listing object (instantBooking, checked, bills, allowsPermanentContract, isBestChoice, plan)
 * @param {boolean} params.hasNoDeposit - from usePricingContext
 * @param {Object} params.landlordInformation - from useLandlordInformation (optional, { type } for landlord type)
 * @returns {Array<{ name: string, icon: string, value: boolean, include: boolean }>}
 */
export const getValuePropositionChips = ({ listing, hasNoDeposit = false }) => {
  const {
    instantBooking = {},
    checked = false,
    bills = {},
    allowsPermanentContract = false
  } = listing;
  const { isEnabled: instantBookingEnabled = false } = instantBooking;
  const billsIncluded = getBillsIncludedUtil(bills);
  const isBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.all;
  const isSomeBillsIncluded = billsIncluded === BILLS_INCLUDED_VALUES.some;
  const showDepositProtection = !hasNoDeposit;

  const chips = [
    {
      name: trans('listing.labels.noDeposit.title'),
      icon: VALUE_PROPOSITION_ICONS.noDeposit,
      value: true,
      include: hasNoDeposit,
      onClick: () =>
        handleSpotahomeGuaranteeClick({
          title: trans('listing.labels.noDeposit.title'),
          body: trans('listing.labels.noDeposit.description')
        })
    },
    {
      name: trans('listing.labels.bills.all.title'),
      icon: VALUE_PROPOSITION_ICONS.billsIncluded,
      value: true,
      include: isBillsIncluded,
      onClick: () =>
        handleSpotahomeGuaranteeClick({
          title: trans('listing.labels.bills.all.title'),
          body: trans('listing.labels.bills.description')
        })
    },
    {
      name: trans('listing.labels.bills.some.title'),
      icon: VALUE_PROPOSITION_ICONS.someBillsIncluded,
      value: true,
      include: isSomeBillsIncluded,
      onClick: () =>
        handleSpotahomeGuaranteeClick({
          title: trans('listing.labels.bills.some.title'),
          body: trans('listing.labels.bills.description')
        })
    },
    {
      name: trans('property_show.book_now_card.instant_booking'),
      icon: VALUE_PROPOSITION_ICONS.instantBooking,
      value: true,
      include: instantBookingEnabled,
      onClick: handleInstantBookingClick
    },
    {
      name: trans('homecard.label.long-term'),
      icon: VALUE_PROPOSITION_ICONS.longTerm,
      value: true,
      include: allowsPermanentContract,
      onClick: () =>
        handleSpotahomeGuaranteeClick({
          title: trans('homecard.label.long-term'),
          body: () => (
            <>
              <p>{trans('listing.labels.long.term.description')}</p>
              <p>{trans('search.booknow-card-label.tooltip.permanent.es')}</p>
            </>
          )
        })
    },
    {
      name: trans('homecard.label.deposit.protected'),
      icon: VALUE_PROPOSITION_ICONS.depositProtection,
      value: true,
      include: showDepositProtection,
      onClick: handleDepositProtectionClick
    },
    {
      name: trans('property_show.book_now_card.checked'),
      icon: VALUE_PROPOSITION_ICONS.isChecked,
      value: true,
      include: checked,
      onClick: handleVerificationClick
    },
    {
      name: trans(
        'property_show.book_now_card.grace.period.cancellation.title'
      ),
      icon: VALUE_PROPOSITION_ICONS.gracePeriodCancellation,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: handleGracePeriodCancellationClick
    },
    {
      name: trans('listing.landlord_info.verified_landlord'),
      icon: VALUE_PROPOSITION_ICONS.landlordVerified,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: handleVerificationClick
    },
    {
      name: trans('property_show.book_now_card.24h'),
      icon: VALUE_PROPOSITION_ICONS.check24Hours,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: handleCheck24HoursClick
    },
    {
      name: trans('spotahome.guarantee.last.minute.backup'),
      icon: VALUE_PROPOSITION_ICONS.lastMinuteBackUp,
      value: true,
      include: true,
      isGuarantee: true,
      onClick: handleLastMinuteBackupClick
    }
  ];

  return chips.filter(chip => chip.include);
};
